import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { Calendar, ChartBar, CurrencyInr, Wallet, ClockCounterClockwise, Books, FileText, ShoppingCart, Receipt, Bank, ClockAfternoon, ArrowLeft, SignOut, House } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import MetricCard from "@/components/MetricCard";
import EnhancedQuickEntryForm from "@/components/EnhancedQuickEntryForm";
import TransactionsTable from "@/components/TransactionsTable";
import WeeklyChart from "@/components/WeeklyChart";
import PendingPaymentsTable from "@/components/PendingPaymentsTable";
import DatePicker from "@/components/DatePicker";
import LedgerSidePanel from "@/components/LedgerSidePanel";
import ReportsSection from "@/components/ReportsSection";
import SalesDetailView from "@/components/SalesDetailView";
import ExpensesDetailView from "@/components/ExpensesDetailView";
import CreditsDetailView from "@/components/CreditsDetailView";
import PendingPaymentsDetailView from "@/components/PendingPaymentsDetailView";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateStr) => {
  const [day, month, year] = dateStr.split('/');
  return `${day}/${month}/${year}`;
};

export default function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dashboardData, setDashboardData] = useState(null);
  const [weeklyTrend, setWeeklyTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [isLedgerOpen, setIsLedgerOpen] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // dashboard, sales, expenses, credits, pending

  const dateString = format(selectedDate, 'dd/MM/yyyy');

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/dashboard/${dateString}`);
      setDashboardData(response.data);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [dateString]);

  const fetchWeeklyTrend = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/weekly-trend`);
      setWeeklyTrend(response.data);
    } catch (err) { toast.error("Failed to load weekly trend"); }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchWeeklyTrend();
  }, [fetchDashboardData, fetchWeeklyTrend]);

  const handleEntryAdded = () => {
    fetchDashboardData();
    fetchWeeklyTrend();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#e8ecf1]">
        <div className="shimmer w-96 h-32 rounded-xl"></div>
      </div>
    );
  }

  // Route to different views
  if (currentView === 'sales') {
    return <SalesDetailView onBack={() => setCurrentView('dashboard')} />;
  }
  
  if (currentView === 'expenses') {
    return <ExpensesDetailView onBack={() => setCurrentView('dashboard')} />;
  }
  
  if (currentView === 'credits') {
    return <CreditsDetailView onBack={() => setCurrentView('dashboard')} />;
  }
  
  if (currentView === 'pending') {
    return <PendingPaymentsDetailView onBack={() => setCurrentView('dashboard')} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7fa] to-[#e8ecf1]">
      <LedgerSidePanel isOpen={isLedgerOpen} onClose={() => setIsLedgerOpen(false)} />

      {/* Premium Header */}
      <header className="glass sticky top-0 z-50 border-b border-white/20 backdrop-blur-xl">
        <div className="px-4 py-4 lg:px-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="premium-button bg-gradient-to-r from-gray-600 to-gray-700 text-white"
                data-testid="home-button"
              >
                <House size={20} weight="bold" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsLedgerOpen(true)}
                className="premium-button bg-gradient-to-r from-purple-500 to-indigo-600 text-white"
                data-testid="open-ledger-button"
              >
                <Books size={20} weight="bold" />
              </Button>
              <h1 className="heading text-3xl lg:text-4xl font-black gradient-text" data-testid="dashboard-title">
                Expenses & Accounts
              </h1>
              <span className="premium-badge hidden sm:inline-flex">PRO</span>
            </div>
            <div className="flex items-center gap-3">
              <DatePicker
                selected={selectedDate}
                onSelect={setSelectedDate}
              />
              <span className="date-text text-sm text-gray-600 font-semibold" data-testid="selected-date-display">
                {format(selectedDate, 'dd/MM/yyyy')}
              </span>
              {onLogout && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLogout}
                  className="text-gray-500 hover:text-red-600"
                  data-testid="dashboard-logout-btn"
                >
                  <SignOut size={18} />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-3 py-4 sm:px-4 sm:py-6 lg:px-6 max-w-[1800px] mx-auto">
        {/* Quick Navigation */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <Button
            onClick={() => setCurrentView('sales')}
            className="premium-button premium-card h-16 sm:h-24 flex-col gap-1 sm:gap-2 bg-gradient-to-br from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border-2 border-green-200"
            data-testid="nav-sales"
          >
            <ShoppingCart size={24} className="text-green-600 sm:w-8 sm:h-8" weight="bold" />
            <span className="font-bold text-green-700 text-xs sm:text-sm">Sales</span>
          </Button>
          
          <Button
            onClick={() => setCurrentView('expenses')}
            className="premium-button premium-card h-16 sm:h-24 flex-col gap-1 sm:gap-2 bg-gradient-to-br from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100 border-2 border-red-200"
            data-testid="nav-expenses"
          >
            <Receipt size={24} className="text-red-600 sm:w-8 sm:h-8" weight="bold" />
            <span className="font-bold text-red-700 text-xs sm:text-sm">Expenses</span>
          </Button>
          
          <Button
            onClick={() => setCurrentView('credits')}
            className="premium-button premium-card h-16 sm:h-24 flex-col gap-1 sm:gap-2 bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-2 border-blue-200"
            data-testid="nav-credits"
          >
            <Bank size={24} className="text-blue-600 sm:w-8 sm:h-8" weight="bold" />
            <span className="font-bold text-blue-700 text-xs sm:text-sm">Credits</span>
          </Button>
          
          <Button
            onClick={() => setCurrentView('pending')}
            className="premium-button premium-card h-16 sm:h-24 flex-col gap-1 sm:gap-2 bg-gradient-to-br from-orange-50 to-yellow-50 hover:from-orange-100 hover:to-yellow-100 border-2 border-orange-200"
            data-testid="nav-pending"
          >
            <ClockAfternoon size={24} className="text-orange-600 sm:w-8 sm:h-8" weight="bold" />
            <span className="font-bold text-orange-700 text-xs sm:text-sm">Pending</span>
          </Button>
        </div>

        {/* Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-6">
          <div className="premium-card stat-card p-3 sm:p-6 smooth-transition cursor-pointer" onClick={() => setCurrentView('sales')}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <span className="text-xs sm:text-sm font-semibold text-gray-600">Sales</span>
              <ShoppingCart size={20} className="text-green-500" weight="bold" />
            </div>
            <div className="currency text-lg sm:text-3xl font-black gradient-success">{formatCurrency(dashboardData?.total_sales || 0)}</div>
          </div>
          
          <div className="premium-card stat-card p-3 sm:p-6 smooth-transition cursor-pointer" onClick={() => setCurrentView('expenses')}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <span className="text-xs sm:text-sm font-semibold text-gray-600">Expenses</span>
              <Receipt size={20} className="text-red-500" weight="bold" />
            </div>
            <div className="currency text-lg sm:text-3xl font-black gradient-danger">{formatCurrency(dashboardData?.total_expenses || 0)}</div>
          </div>
          
          <div className="premium-card stat-card p-3 sm:p-6 smooth-transition cursor-pointer" onClick={() => setCurrentView('credits')}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <span className="text-xs sm:text-sm font-semibold text-gray-600">Cash + Bank</span>
              <Bank size={20} className="text-blue-500" weight="bold" />
            </div>
            <div className="currency text-lg sm:text-3xl font-black gradient-text">{formatCurrency(dashboardData?.cash_bank_balance || 0)}</div>
          </div>
          
          <div className="premium-card stat-card p-3 sm:p-6 smooth-transition cursor-pointer" onClick={() => setCurrentView('pending')}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <span className="text-xs sm:text-sm font-semibold text-gray-600">Pending</span>
              <ClockAfternoon size={20} className="text-orange-500" weight="bold" />
            </div>
            <div className="currency text-lg sm:text-3xl font-black gradient-warning">{formatCurrency(dashboardData?.total_pending || 0)}</div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* Left Column - Charts and Tables */}
          <div className="lg:col-span-8 space-y-6">
            {/* Tabs for different views */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-start border-b border-[#E5E5E0] rounded-none bg-white p-0 h-auto">
                <TabsTrigger 
                  value="overview" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent px-6 py-3"
                  data-testid="tab-overview"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger 
                  value="transactions" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent px-6 py-3"
                  data-testid="tab-transactions"
                >
                  Transactions
                </TabsTrigger>
                <TabsTrigger 
                  value="pending" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent px-6 py-3"
                  data-testid="tab-pending"
                >
                  Pending Payments
                </TabsTrigger>
                <TabsTrigger 
                  value="reports" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent px-6 py-3"
                  data-testid="tab-reports"
                >
                  <FileText size={16} className="mr-2" />
                  Reports
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6 space-y-6">
                {/* Weekly Chart */}
                <Card className="rounded-none border border-[#E5E5E0] bg-white p-6" data-testid="weekly-chart-container">
                  <h2 className="heading text-lg font-bold mb-4 text-[#111110]">7-Day Trend</h2>
                  <WeeklyChart data={weeklyTrend} />
                </Card>

                {/* Recent Transactions */}
                <Card className="rounded-none border border-[#E5E5E0] bg-white p-6" data-testid="recent-transactions-container">
                  <h2 className="heading text-lg font-bold mb-4 text-[#111110]">Recent Transactions</h2>
                  <TransactionsTable data={dashboardData} />
                </Card>
              </TabsContent>

              <TabsContent value="transactions" className="mt-6">
                <Card className="rounded-none border border-[#E5E5E0] bg-white p-6" data-testid="all-transactions-container">
                  <h2 className="heading text-lg font-bold mb-4 text-[#111110]">All Transactions - {dateString}</h2>
                  <TransactionsTable data={dashboardData} showAll />
                </Card>
              </TabsContent>

              <TabsContent value="pending" className="mt-6">
                <Card className="rounded-none border border-[#E5E5E0] bg-white p-6" data-testid="pending-payments-container">
                  <h2 className="heading text-lg font-bold mb-4 text-[#111110]">Pending Payments</h2>
                  <PendingPaymentsTable 
                    payments={dashboardData?.pending_payments || []} 
                    onUpdate={handleEntryAdded}
                  />
                </Card>
              </TabsContent>

              <TabsContent value="reports" className="mt-6">
                <ReportsSection />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Quick Entry Form */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <Card className="premium-card p-6 smooth-transition" data-testid="quick-entry-panel">
                <h2 className="heading text-lg font-bold mb-4 gradient-text">✨ Quick Entry</h2>
                <EnhancedQuickEntryForm 
                  selectedDate={dateString} 
                  onEntryAdded={handleEntryAdded}
                />
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
